/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerCheckInPage — /organizer/check-in
 * ------------------------------------------
 * The one global check-in desk. It is not tied to an event or an activity:
 * any pass for any of the organizer's events open for check-in can be scanned
 * here, one after another, and the pass says which event and activity it is for.
 *
 * The rules live in runScan (order of checks) and checkInRules (time windows);
 * this page only collects the input, runs the scan and draws the outcome.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import useCheckInDesk, { checkInApi } from '../../hooks/useCheckInDesk';
import { isUserLoggedIn } from '../../utils/utils';
import runScan, { confirmCheckOut } from '../../components/organizer/checkin/runScan';
import QrScanner from '../../components/organizer/checkin/QrScanner';
import ScanResult from '../../components/organizer/checkin/ScanResult';
import RecentScans from '../../components/organizer/checkin/RecentScans';
import EventPicker from '../../components/organizer/checkin/EventPicker';
import CheckInHistory from '../../components/organizer/checkin/CheckInHistory';
import CertificatesView from '../../components/organizer/checkin/CertificatesView';
import { eventWindow, OPENS_BEFORE_MIN } from '../../components/organizer/checkin/checkInRules';
import { formatDateTime } from '../../components/athlohive/formatDateTime';

const WIDE_QUERY = '(min-width: 900px)';

function useIsWide() {
  const [wide, setWide] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE_QUERY).matches : true
  ));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return wide;
}

/** The next of the organizer's events to open for check-in, for the empty state. */
function nextOpening(events, now) {
  return events
    .map((event) => ({ event, timing: eventWindow(event, now) }))
    .filter(({ event, timing }) => timing.opensAt && timing.opensAt > now
      && !['DRAFT', 'CANCELLED'].includes(String(event.status || '').toUpperCase()))
    .sort((a, b) => a.timing.opensAt - b.timing.opensAt)[0] || null;
}

/** The Scanner tab: the one global check-in desk. */
function ScannerView({ header }) {
  const desk = useCheckInDesk();
  const wide = useIsWide();
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(null); // the input a confirm/choose answer reruns
  const [session, setSession] = useState({ ok: 0, problems: 0, last: null });
  const [recentOpen, setRecentOpen] = useState(false);
  const [scopeId, setScopeId] = useState(null); // the event All scans shows; null = every open event
  const userIdRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    isUserLoggedIn().then((who) => { userIdRef.current = who?.user_id ?? null; }).catch(() => {});
  }, []);

  // One request at a time: a scan, or a Check out tap on the card.
  const run = async (work) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setResult(null);
    try {
      const now = new Date();
      const outcome = await work(now);
      setResult(outcome);
      if (outcome.kind === 'ok') {
        desk.recordScan(outcome.scan);
        setSession((s) => ({ ...s, ok: s.ok + 1, last: now }));
        setPasscode('');
      } else if (outcome.kind === 'stop' || outcome.kind === 'error') {
        setSession((s) => ({ ...s, problems: s.problems + 1, last: now }));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const scan = (input, extra = {}) => run(async (now) => {
    const outcome = await runScan({
      api: checkInApi,
      input,
      now,
      eventsById: desk.eventsById,
      activitiesById: desk.activitiesById,
      openEventIds: desk.openIds,
      userId: userIdRef.current,
      ...extra,
    });
    setPending(outcome.kind === 'confirm' || outcome.kind === 'choose' ? input : null);
    return outcome;
  });
  const checkOut = () => {
    const shown = result;
    if (!shown?.checkOut) return;
    run((now) => confirmCheckOut({
      api: checkInApi, result: shown, eventsById: desk.eventsById, now, userId: userIdRef.current,
    }));
  };

  const onQr = (text) => scan({ qrToken: text });
  const onPasscode = (event) => {
    event.preventDefault();
    const code = passcode.trim().toUpperCase();
    if (code) scan({ passcode: code });
  };
  const confirmLate = () => pending && scan(pending, { lateConfirmed: true, eventId: result?.eventId });
  const chooseEvent = (eventId) => pending && scan(pending, { eventId });

  if (desk.isLoading) {
    return (
      <div className="ochk-page">
        {header}
        <div className="org-content"><div className="org-empty"><p>Loading your events…</p></div></div>
      </div>
    );
  }

  if (desk.isError) {
    return (
      <div className="ochk-page">
        {header}
        <div className="org-content">
          <div className="org-empty" role="alert">
            <h2>Couldn’t load your events</h2>
            <p>The scanner needs your events to check times and show names.</p>
            <button type="button" className="org-btn" style={{ marginTop: 14 }} onClick={desk.retry}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  if (desk.open.length === 0) {
    const next = nextOpening(desk.events, desk.now);
    return (
      <div className="ochk-page">
        {header}
        <div className="org-content">
          <div className="org-empty">
            <h2>Nothing is open for check-in right now</h2>
            <p>
              Check-in opens {OPENS_BEFORE_MIN} minutes before each event starts.
              {next && (
                <>
                  {' '}Next: <b>{next.event.title}</b>, opening{' '}
                  {formatDateTime(next.timing.opensAt.toISOString(), next.event.timezone)}.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // All scans follows the event picked in the dropdown; an event that has
  // since closed falls back to all of them. With one event open, "all" is it.
  const scope = desk.openIds.includes(scopeId) ? scopeId : 'all';
  const picked = scope !== 'all' ? desk.eventsById[scope] : (desk.open.length === 1 ? desk.open[0] : null);
  const recentList = (
    <RecentScans
      openIds={desk.openIds}
      scope={scope}
      eventsById={desk.eventsById}
      checkedIn={desk.total}
      onClose={wide ? undefined : () => setRecentOpen(false)}
      onRefresh={desk.refreshCounts}
    />
  );

  return (
    <div className="ochk-page">
      {header}
      <div className="org-content ochk">
        <div className="ochk-bar">
          <div className="ochk-open">
            <span className="org-lab">Open for check-in</span>
            <EventPicker
              groups={{ open: desk.open, finished: [], upcoming: [] }}
              selected={picked}
              onSelect={(id) => { setScopeId(id); if (!wide && id != null) setRecentOpen(true); }}
              all={desk.open.length > 1}
              note={scope === 'all' && desk.total != null ? `${desk.total} in` : ''}
              title="Open for check-in"
            />
          </div>
          <p className="ochk-hint">
            <span className="ochk-hinticon" aria-hidden="true"><i className="bi bi-qr-code-scan" /></span>
            <span><b>Scan once</b> to check in · <b>scan again</b> when they leave</span>
          </p>
        </div>

        <div className="ochk-grid">
          <div className="ochk-col">
            {/* Not paused while a question is showing: the next scan replaces it. */}
            <QrScanner onScan={onQr} paused={busy} />
            <form className="ochk-manual" onSubmit={onPasscode} autoComplete="off">
              <label htmlFor="ochk-passcode">Or type the passcode</label>
              <div>
                <input
                  id="ochk-passcode"
                  className="orgf-input ochk-codeinput"
                  value={passcode}
                  onChange={(event) => setPasscode(event.target.value.toUpperCase())}
                  maxLength={8}
                  placeholder="K7Q2M9XA"
                  inputMode="text"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
                <button type="submit" className="org-btn" disabled={busy || !passcode.trim()}>Check</button>
              </div>
            </form>
          </div>

          <div className="ochk-col">
            <ScanResult
              result={result}
              busy={busy}
              eventsById={desk.eventsById}
              onConfirm={confirmLate}
              onChoose={chooseEvent}
              onCheckOut={checkOut}
              onDismiss={() => setResult(null)}
            />
            {wide ? recentList : (
              <button type="button" className="ochk-summary" onClick={() => setRecentOpen(true)}>
                <span className="ochk-summarytext">
                  <b>All scans</b>
                  <span>
                    <em className="is-ok">{session.ok} in</em>
                    {' · '}
                    <em className="is-bad">{session.problems} need a look</em>
                    {session.last && ` · last ${session.last.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}`}
                  </span>
                </span>
                <span className="ochk-summarygo">View all <i className="bi bi-chevron-right" aria-hidden="true" /></span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/*
        Portalled into the organizer shell: inside the main column it could never
        rise above the rail (the column is its own stacking context), and the
        shell is where the --org-* tokens are set, so the colours still apply.
      */}
      {!wide && recentOpen && createPortal(
        <div className="ochk-sheet" role="dialog" aria-modal="true" aria-label="All scans">
          {recentList}
        </div>,
        document.querySelector('.org-shell') || document.body
      )}
    </div>
  );
}

const VIEWS = [
  { id: 'scanner', label: 'Scanner', icon: 'bi-qr-code-scan', sub: 'One scanner for all your events and activities' },
  { id: 'history', label: 'History', icon: 'bi-clock-history', sub: 'Results for any of your events, including finished ones' },
  { id: 'certificates', label: 'Certificates', icon: 'bi-award', sub: 'E-certificates for participants who checked in' },
];

/**
 * /organizer/check-in — three tabs on one page. Scanner is the default; History
 * is ?view=history and Certificates ?view=certificates, so each survives a
 * reload and can be linked to.
 */
function OrganizerCheckInPage() {
  const [params, setParams] = useSearchParams();
  const view = ['history', 'certificates'].includes(params.get('view')) ? params.get('view') : 'scanner';
  const current = VIEWS.find((item) => item.id === view);

  const header = (
    <header className="org-topbar ochk-topbar">
      <div>
        {/* <h1>Check-in</h1> */}
        <h1>Participants</h1>
        <p className="org-sub">{current.sub}</p>
      </div>
      <nav className="ochk-tabs" aria-label="Check-in views">
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-current={view === item.id ? 'page' : undefined}
            onClick={() => setParams(item.id === 'scanner' ? {} : { view: item.id }, { replace: true })}
          >
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            {item.label}
          </button>
        ))}
      </nav>
    </header>
  );

  if (view === 'certificates') {
    return (
      <div className="ochk-page">
        {header}
        <CertificatesView />
      </div>
    );
  }
  if (view === 'history') {
    return (
      <div className="ochk-page">
        {header}
        <CheckInHistory />
      </div>
    );
  }
  return <ScannerView header={header} />;
}

export default OrganizerCheckInPage;
